import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../services/api';
import { overlayDismiss } from '../utils/modalDismiss';
import QrPanel from '../components/QrPanel';
import { STATUS_BADGES, formatWindow, formatTime, toDateTimeLocal } from '../utils/attendance';

const REFRESH_MS = 15000;
const AttendanceDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [error, setError] = useState('');
  const [manual, setManual] = useState({ name: '', employee_id: '', email: '' });
  const [editForm, setEditForm] = useState(null);
  const [trainings, setTrainings] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/attendance/${id}`);
      setSession(data);
      setError('');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load attendance');
    }
  }, [id]);

  // Keep the list live while the trainer watches people scan in.
  useEffect(() => {
    load();
    const timer = setInterval(load, REFRESH_MS);
    return () => clearInterval(timer);
  }, [load]);

  if (!session) {
    return error
      ? <div className="alert alert-error">{error}</div>
      : <div className="text-center mt-5">Loading...</div>;
  }

  const link = `${window.location.origin}/attend/${session.token}`;
  const status = STATUS_BADGES[session.status];

  const addManual = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/attendance/${id}/records`, manual);
      setManual({ name: '', employee_id: '', email: '' });
      load();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to add');
    } finally {
      setBusy(false);
    }
  };

  const removeRecord = async (r) => {
    if (!window.confirm(`Remove ${r.name} (${r.employee_id}) from this attendance?`)) return;
    try {
      await api.delete(`/attendance/${id}/records/${r.id}`);
      load();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to remove');
    }
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/attendance/${id}`, {
        title: editForm.title,
        starts_at: new Date(editForm.starts_at).toISOString(),
        ends_at: new Date(editForm.ends_at).toISOString(),
        training_id: editForm.training_id || null
      });
      setEditForm(null);
      load();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to save');
    }
  };

  const deleteSession = async () => {
    if (!window.confirm(`Delete "${session.title}" and all ${session.records.length} attendance record(s)? This cannot be undone.`)) return;
    try {
      await api.delete(`/attendance/${id}`);
      navigate(session.training_id ? `/attendance/trainings/${session.training_id}` : '/attendance');
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete');
    }
  };

  const download = async () => {
    try {
      const response = await api.get(`/attendance/${id}/export`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Attendance_${session.title.replace(/[^a-zA-Z0-9]+/g, '_')}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      alert('Failed to download attendance');
    }
  };

  return (
    <div>
      {session.training_id
        ? <Link to={`/attendance/trainings/${session.training_id}`} className="text-muted text-sm">← {session.training_title}</Link>
        : <Link to="/attendance" className="text-muted text-sm">← All attendance</Link>}
      <div className="flex flex-between flex-center mb-4 mt-2">
        <div>
          <h1 style={{ marginBottom: '4px' }}>{session.title}</h1>
          <p className="text-muted">
            {formatWindow(session.starts_at, session.ends_at)}
            {session.category_name && ` · ${session.category_name}`}
            {' '}<span className={`badge ${status.className}`}>{status.label}</span>
          </p>
        </div>
        <div className="flex gap-2">
          <button className="btn btn-secondary btn-sm" onClick={() => {
            setEditForm({
              title: session.title,
              starts_at: toDateTimeLocal(session.starts_at),
              ends_at: toDateTimeLocal(session.ends_at),
              training_id: session.training_id || ''
            });
            api.get('/attendance/trainings').then(r => setTrainings(r.data)).catch(() => {});
          }}>Edit</button>
          <button className="btn btn-danger btn-sm" onClick={deleteSession}>Delete</button>
        </div>
      </div>

      {error && <div className="alert alert-error mb-4">{error}</div>}

      <div className="grid grid-2" style={{ alignItems: 'start' }}>
        {/* QR code */}
        <QrPanel
          link={link}
          title={session.title}
          subtitle={formatWindow(session.starts_at, session.ends_at)}
          instruction="Scan with your phone camera to mark your attendance"
          fileName={`Attendance_QR_${session.title}`}
          fullScreenFooter={(
            <p style={{ fontSize: '24px', marginTop: '20px' }}>
              <strong>{session.records.length}</strong> present
            </p>
          )}
        />

        {/* Manual entry */}
        <div className="card">
          <h3 style={{ marginTop: 0 }}>Add someone manually</h3>
          <p className="text-muted text-sm">For a trainee who cannot scan. Works at any time.</p>
          <form onSubmit={addManual}>
            <div className="form-group">
              <label>Name</label>
              <input type="text" required maxLength={255} value={manual.name}
                onChange={e => setManual({ ...manual, name: e.target.value })} />
            </div>
            <div className="form-group">
              <label>Employee ID</label>
              <input type="text" required maxLength={50} value={manual.employee_id}
                onChange={e => setManual({ ...manual, employee_id: e.target.value })} />
            </div>
            <div className="form-group">
              <label>Email</label>
              <input type="email" required maxLength={255} value={manual.email}
                onChange={e => setManual({ ...manual, email: e.target.value })} />
            </div>
            <button type="submit" className="btn btn-primary" disabled={busy}>Add</button>
          </form>
        </div>
      </div>

      {/* Present list */}
      <div className="card mt-4" style={{ padding: 0 }}>
        <div className="flex flex-between flex-center" style={{ padding: '16px 20px' }}>
          <h3 style={{ margin: 0 }}>Present: {session.records.length}</h3>
          <div className="flex gap-2 flex-center">
            <span className="text-muted text-sm">Updates every 15 seconds</span>
            <button className="btn btn-success btn-sm" onClick={download}>Download Excel</button>
          </div>
        </div>
        {session.records.length === 0 ? (
          <p className="text-muted text-center" style={{ padding: '0 20px 20px' }}>Nobody has marked in yet.</p>
        ) : (
          <table className="table">
            <thead>
              <tr><th>#</th><th>Employee ID</th><th>Name</th><th>Email</th><th>Time</th><th>How</th><th></th></tr>
            </thead>
            <tbody>
              {session.records.map((r, i) => (
                <tr key={r.id}>
                  <td>{i + 1}</td>
                  <td><code>{r.employee_id}</code></td>
                  <td>{r.name}</td>
                  <td className="text-sm">{r.email || <span className="text-muted">—</span>}</td>
                  <td>{formatTime(r.marked_at)}</td>
                  <td className="text-sm text-muted">
                    {r.source === 'manual' ? `Added by ${r.added_by_name || 'trainer'}` : 'QR scan'}
                  </td>
                  <td><button className="btn btn-danger btn-sm" onClick={() => removeRecord(r)}>Remove</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {editForm && (
        <div className="modal-overlay" {...overlayDismiss(() => setEditForm(null))}>
          <div className="modal">
            <div className="modal-header">
              <h3>Edit Attendance</h3>
              <button className="modal-close" onClick={() => setEditForm(null)}>&times;</button>
            </div>
            <form onSubmit={saveEdit}>
              <div className="modal-body">
                <div className="form-group">
                  <label>Title</label>
                  <input type="text" required maxLength={255} value={editForm.title}
                    onChange={e => setEditForm({ ...editForm, title: e.target.value })} />
                </div>
                <div className="grid grid-2">
                  <div className="form-group">
                    <label>Opens</label>
                    <input type="datetime-local" required value={editForm.starts_at}
                      onChange={e => setEditForm({ ...editForm, starts_at: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label>Closes</label>
                    <input type="datetime-local" required value={editForm.ends_at}
                      onChange={e => setEditForm({ ...editForm, ends_at: e.target.value })} />
                  </div>
                </div>
                <div className="form-group">
                  <label>Training</label>
                  <select value={editForm.training_id}
                    onChange={e => setEditForm({ ...editForm, training_id: e.target.value })}>
                    <option value="">— None (single day) —</option>
                    {trainings.map(t => <option key={t.id} value={t.id}>{t.title}</option>)}
                  </select>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setEditForm(null)}>Cancel</button>
                <button type="submit" className="btn btn-primary">Save</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AttendanceDetail;
